import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Tooltip,
  Box,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Schedule as ScheduleIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { FollowStatus } from '../../types/follow';
import { useFollowButton } from '../../hooks/useFollowButton';
import { useAuth } from '../../contexts/AuthContext';
import AuthPrompt from '../Auth/AuthPrompt';

// Ensure this file is treated as a module
export { };

interface FollowButtonProps {
  targetUserId: string;
  initialFollowStatus?: FollowStatus;
  size?: 'small' | 'medium' | 'large';
  variant?: 'contained' | 'outlined' | 'text';
  fullWidth?: boolean;
  showIcon?: boolean;
  onFollowChange?: (isFollowing: boolean, status: FollowStatus) => void;
  className?: string;
}

/**
 * Follow button component with different states (follow/unfollow/pending)
 * Uses centralized useFollowButton hook for consistent logic across all follow buttons
 */
const FollowButton: React.FC<FollowButtonProps> = ({
  targetUserId,
  initialFollowStatus,
  size = 'medium',
  variant = 'contained',
  fullWidth = false,
  showIcon = true,
  onFollowChange,
  className,
}) => {
  const { user: currentUser, isAuthenticated } = useAuth();
  const [showAuthPrompt, setShowAuthPrompt] = useState<boolean>(false);
  const {
    followStatus,
    blockStatus,
    buttonState,
    handleFollowAction: hookHandleFollowAction,
    shouldRender,
  } = useFollowButton({
    targetUserId,
    initialFollowStatus,
  });

  // Wrap the hook's handleFollowAction to include authentication check and onFollowChange callback
  const handleFollowAction = async () => {
    // Check authentication first
    if (!isAuthenticated) {
      setShowAuthPrompt(true);
      return;
    }

    const previousStatus = followStatus;
    await hookHandleFollowAction();

    // Call the callback if provided (note: followStatus will be updated by the hook)
    if (onFollowChange && previousStatus) {
      // We need to determine the new status based on the action
      if (previousStatus.isFollowing || previousStatus.requestStatus === 'pending') {
        // Was unfollowing
        const newStatus: FollowStatus = {
          isFollowing: false,
          isFollowedBy: previousStatus.isFollowedBy,
          relationship: previousStatus.isFollowedBy ? 'follower' : 'none',
        };
        onFollowChange(false, newStatus);
      } else {
        // Was following - we'd need to know if it requires approval
        // For now, assume it doesn't require approval
        const newStatus: FollowStatus = {
          isFollowing: true,
          isFollowedBy: previousStatus.isFollowedBy,
          relationship: previousStatus.isFollowedBy ? 'mutual' : 'following',
        };
        onFollowChange(true, newStatus);
      }
    }
  };

  // Get button icon based on state
  const getButtonIcon = () => {
    if (!showIcon) {
      return null;
    }

    if (buttonState.isLoading) {
      return <CircularProgress size={16} color="inherit" />;
    }

    switch (followStatus?.relationship) {
      case 'following':
      case 'mutual':
        return <CheckIcon />;
      case 'pending':
        return <ScheduleIcon />;
      case 'follower':
        return <PersonAddIcon />;
      case 'none':
      default:
        return followStatus?.isFollowing ? <PersonRemoveIcon /> : <PersonAddIcon />;
    }
  };

  // Get tooltip text
  const getTooltipText = () => {
    if (blockStatus?.isBlocked) {
      return 'You have blocked this user';
    }
    if (blockStatus?.isBlockedBy) {
      return 'This user has blocked you';
    }

    switch (followStatus?.relationship) {
      case 'following':
        return 'Click to unfollow';
      case 'mutual':
        return 'You follow each other';
      case 'pending':
        return 'Follow request pending - click to cancel';
      case 'follower':
        return 'This user follows you - click to follow back';
      case 'none':
      default:
        return 'Click to follow this user';
    }
  };

  // Handle hover effects for following state
  const [isHovered, setIsHovered] = useState(false);
  const displayText = (followStatus?.isFollowing && isHovered) ? 'Unfollow' : buttonState.buttonText;
  const displayColor = (followStatus?.isFollowing && isHovered) ? 'error' : buttonState.buttonColor;

  // BULLETPROOF USER ID VALIDATION - MULTIPLE LAYERS OF PROTECTION
  // This prevents follow buttons from rendering when currentUser.id === targetUserId
  // But allows rendering for anonymous users (they'll get auth prompt)

  // Layer 1: Check if authenticated user is same as target
  if (isAuthenticated && currentUser && currentUser.id === targetUserId) {
    console.log('❌ FollowButton: Not rendering - same user');
    return null;
  }

  // Layer 2: Check if no target user
  if (!targetUserId) {
    console.log('❌ FollowButton: Not rendering - no target user ID');
    return null;
  }

  // Debug logging for successful validation
  console.log('🔍 FollowButton Render Debug:', {
    targetUserId,
    currentUserId: currentUser?.id,
    isAuthenticated,
    shouldRender,
    followStatus,
    buttonState,
    blockStatus,
    passedValidation: true
  });

  // For anonymous users, show a simplified follow button
  if (!isAuthenticated) {
    return (
      <>
        <Tooltip title="Sign in to follow users" arrow>
          <Box className={className}>
            <Button
              variant={variant}
              color="primary"
              size={size}
              fullWidth={fullWidth}
              onClick={handleFollowAction}
              startIcon={showIcon ? <PersonAddIcon /> : null}
              sx={{
                minWidth: fullWidth ? undefined : 100,
                textTransform: 'none',
                fontWeight: 600,
                transition: 'all 0.2s ease-in-out',
                backgroundColor: variant === 'contained' ? 'primary.main' : 'transparent',
                color: variant === 'contained' ? 'primary.contrastText' : 'primary.main',
                border: variant === 'outlined' ? '2px solid' : 'none',
                borderColor: 'primary.main',
                boxShadow: variant === 'contained' ? '0 4px 12px rgba(46, 125, 50, 0.3)' : 'none',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  backgroundColor: variant === 'contained' ? 'primary.dark' : 'rgba(46, 125, 50, 0.1)',
                  boxShadow: variant === 'contained' ? '0 6px 20px rgba(46, 125, 50, 0.4)' : 'none',
                },
              }}
            >
              Follow
            </Button>
          </Box>
        </Tooltip>
        
        <AuthPrompt
          open={showAuthPrompt}
          onClose={() => setShowAuthPrompt(false)}
          action="follow users"
          title="Sign In to Follow Users"
        />
      </>
    );
  }

  // Don't render if shouldn't render (hook-level check)
  if (!shouldRender) {
    console.log('❌ FollowButton: Not rendering - shouldRender is false (hook level)');
    return null;
  }

  return (
    <Tooltip title={getTooltipText()} arrow>
      <Box className={className}>
        <Button
          variant={variant}
          color={displayColor}
          size={size}
          fullWidth={fullWidth}
          disabled={buttonState.disabled || buttonState.isLoading}
          onClick={handleFollowAction}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          startIcon={getButtonIcon()}
          sx={{
            minWidth: fullWidth ? undefined : 100,
            textTransform: 'none',
            fontWeight: 600,
            transition: 'all 0.2s ease-in-out',
            // Enhanced visibility on dark marble background
            backgroundColor: variant === 'contained' ? 'primary.main' : 'transparent',
            color: variant === 'contained' ? 'primary.contrastText' : 'primary.main',
            border: variant === 'outlined' ? '2px solid' : 'none',
            borderColor: 'primary.main',
            boxShadow: variant === 'contained' ? '0 4px 12px rgba(46, 125, 50, 0.3)' : 'none',
            '&:hover': {
              transform: 'translateY(-1px)',
              backgroundColor: variant === 'contained' ? 'primary.dark' : 'rgba(46, 125, 50, 0.1)',
              boxShadow: variant === 'contained' ? '0 6px 20px rgba(46, 125, 50, 0.4)' : 'none',
            },
            '&.MuiButton-containedError': {
              backgroundColor: 'error.main',
              boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
              '&:hover': {
                backgroundColor: 'error.dark',
                boxShadow: '0 6px 20px rgba(244, 67, 54, 0.4)',
              },
            },
            '&.MuiButton-outlinedError': {
              borderColor: 'error.main',
              color: 'error.main',
              '&:hover': {
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
              },
            },
          }}
        >
          {displayText}
        </Button>
      </Box>
    </Tooltip>
  );
};

export default FollowButton;